import { useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { ClientProfile, SubscriptionData, PlanData } from '../types';

interface Props {
  profile: ClientProfile;
  subscription: SubscriptionData | null;
  plans: PlanData[];
  onUpdate: (data: Partial<ClientProfile>) => void;
  onRefresh: () => void;
}

const fmtN = (n: number | null | undefined) => (n === -1 || n == null ? '∞' : String(n));

const subStatusColor: Record<string, string> = {
  active: 'bg-emerald-500/20 text-emerald-400',
  trial: 'bg-cyan-500/20 text-cyan-400',
  expired: 'bg-orange-500/20 text-orange-400',
  cancelled: 'bg-red-500/20 text-red-400',
};

const subStatusLabel: Record<string, string> = {
  active: 'Ativa',
  trial: 'Trial',
  expired: 'Vencida',
  cancelled: 'Cancelada',
};

export default function SubscriptionTab({ profile, subscription, plans, onUpdate, onRefresh }: Props) {
  const [selectedPlanId, setSelectedPlanId] = useState(subscription?.plan_id || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const currentPlan = plans.find((p) => p.id === subscription?.plan_id);
  const previewPlan = plans.find((p) => p.id === selectedPlanId);
  const planChanged = selectedPlanId !== subscription?.plan_id;

  const applyPlan = async () => {
    if (!selectedPlanId) return;
    setSaving(true);
    setMsg('');
    const chosen = plans.find((p) => p.id === selectedPlanId);
    if (!chosen) { setMsg('Plano não encontrado.'); setSaving(false); return; }
    const isFree = chosen.price_brl === 0;
    const { error } = await supabase.from('subscriptions').upsert(
      {
        user_id: profile.id,
        plan_id: selectedPlanId,
        status: 'active',
        subscription_type: isFree ? 'free' : 'paid',
        amount: chosen.price_brl,
        billing_cycle: 'monthly',
        started_at: new Date().toISOString(),
        auto_renew: !isFree,
      },
      { onConflict: 'user_id' }
    );
    if (!error) {
      await supabase.from('profiles').update({ plan: isFree ? 'free' : 'paid', updated_at: new Date().toISOString() }).eq('id', profile.id);
      setMsg('Plano alterado com sucesso!');
      onUpdate({ plan: isFree ? 'free' : 'paid' });
      onRefresh();
    } else {
      setMsg('Erro: ' + error.message);
    }
    setSaving(false);
  };

  return (
    <div className="grid grid-cols-3 gap-5">
      <div className="col-span-2 space-y-5">
        {/* Current subscription */}
        {subscription ? (
          <div className="bg-white/5 border border-white/8 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[14px] font-600 text-white flex items-center gap-2">
                <i className="ri-vip-crown-line text-amber-400" />
                Assinatura Atual
              </h3>
              <span className={`text-[11px] font-600 px-2.5 py-1 rounded-full ${subStatusColor[subscription.status] || 'bg-white/10 text-white/40'}`}>
                {subStatusLabel[subscription.status] || subscription.status}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Plano', val: subscription.plan_name },
                { label: 'Valor mensal', val: `R$ ${Number(subscription.amount).toFixed(2)}` },
                { label: 'Ciclo', val: subscription.billing_cycle === 'monthly' ? 'Mensal' : subscription.billing_cycle || 'Mensal' },
                { label: 'Data de início', val: subscription.started_at ? new Date(subscription.started_at).toLocaleDateString('pt-BR') : '—' },
                { label: 'Vencimento', val: subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString('pt-BR') : 'Sem data' },
                { label: 'Auto renovação', val: subscription.auto_renew ? 'Ativada' : 'Desativada' },
              ].map((item) => (
                <div key={item.label} className="bg-white/3 rounded-xl p-3 text-center">
                  <p className="text-[11px] text-white/40 mb-1">{item.label}</p>
                  <p className="text-[13px] font-600 text-white">{item.val}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white/5 border border-white/8 rounded-xl p-8 text-center">
            <i className="ri-vip-crown-line text-3xl text-white/20 mb-2" />
            <p className="text-[14px] font-600 text-white/40">Nenhuma assinatura ativa</p>
            <p className="text-[12px] text-white/25 mt-1">Usuário está no plano Free</p>
          </div>
        )}

        {/* Current plan limits */}
        {currentPlan && (
          <div className="bg-white/5 border border-white/8 rounded-xl p-5">
            <h3 className="text-[14px] font-600 text-white mb-4">Limites do Plano: {currentPlan.name}</h3>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Contas', icon: 'ri-bank-line', val: fmtN(currentPlan.accounts_limit) },
                { label: 'Cartões', icon: 'ri-bank-card-line', val: fmtN(currentPlan.cards_limit) },
                { label: 'Lançamentos/mês', icon: 'ri-exchange-line', val: fmtN(currentPlan.monthly_transactions_limit) },
                { label: 'Metas', icon: 'ri-flag-line', val: fmtN(currentPlan.goals_limit) },
              ].map((item) => (
                <div key={item.label} className="bg-white/3 rounded-xl p-4 text-center">
                  <i className={`${item.icon} text-[20px] text-white/30 mb-2`} />
                  <p className="text-[22px] font-700 text-white">{item.val}</p>
                  <p className="text-[10px] text-white/30 mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Change plan */}
        <div className="bg-white/5 border border-white/8 rounded-xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4 flex items-center gap-2">
            <i className="ri-exchange-line text-violet-400" />
            Alterar Plano
          </h3>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {plans.map((p) => {
              const isCurrentPlan = p.id === subscription?.plan_id;
              const isSelected = selectedPlanId === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPlanId(p.id)}
                  className={`flex items-start justify-between p-4 rounded-xl border text-left transition-all cursor-pointer ${isSelected ? 'border-violet-500 bg-violet-600/15' : 'border-white/10 bg-white/3 hover:bg-white/5'}`}
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-[13px] font-600 text-white">{p.name}</p>
                      {isCurrentPlan && <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">Atual</span>}
                    </div>
                    <p className="text-[12px] text-white/50">R$ {Number(p.price_brl).toFixed(2)}/mês</p>
                    <p className="text-[11px] text-white/30 mt-1">{fmtN(p.accounts_limit)} contas · {fmtN(p.cards_limit)} cartões</p>
                  </div>
                  {isSelected && <i className="ri-check-line text-violet-400 shrink-0" />}
                </button>
              );
            })}
          </div>

          {previewPlan && planChanged && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-[12px] text-amber-300 mb-3">
              <i className="ri-information-line mr-1.5" />
              Isso vai mover o usuário para o plano <strong>{previewPlan.name}</strong> (R$ {Number(previewPlan.price_brl).toFixed(2)}/mês) imediatamente.
            </div>
          )}

          <button
            onClick={applyPlan}
            disabled={saving || !planChanged || !selectedPlanId}
            className="w-full py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
          >
            {saving ? 'Aplicando...' : 'Confirmar alteração de plano'}
          </button>
          {msg && (
            <p className={`text-[12px] text-center mt-2 ${msg.includes('sucesso') ? 'text-emerald-400' : 'text-red-400'}`}>{msg}</p>
          )}
        </div>
      </div>

      {/* Plans sidebar */}
      <div>
        <h3 className="text-[12px] font-600 text-white/40 uppercase tracking-wider mb-3">Comparativo de Planos</h3>
        <div className="space-y-2">
          {plans.map((p) => {
            const isCurrentPlan = p.id === subscription?.plan_id;
            return (
              <div key={p.id} className={`p-4 rounded-xl border ${isCurrentPlan ? 'border-violet-500/40 bg-violet-600/10' : 'border-white/8 bg-white/5'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[13px] font-600 text-white">{p.name}</span>
                  {isCurrentPlan && <span className="text-[10px] bg-violet-600/30 text-violet-300 px-2 py-0.5 rounded-full">Plano atual</span>}
                </div>
                <p className="text-[14px] font-700 text-white mb-2">R$ {Number(p.price_brl).toFixed(2)}<span className="text-[11px] font-400 text-white/30">/mês</span></p>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
                  <span className="text-white/40">Contas: <strong className="text-white/70">{fmtN(p.accounts_limit)}</strong></span>
                  <span className="text-white/40">Cartões: <strong className="text-white/70">{fmtN(p.cards_limit)}</strong></span>
                  <span className="text-white/40">Lançamentos: <strong className="text-white/70">{fmtN(p.monthly_transactions_limit)}</strong></span>
                  <span className="text-white/40">Metas: <strong className="text-white/70">{fmtN(p.goals_limit)}</strong></span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
